/**
 * Force the user back to login when the v2 session is missing/expired (e.g. a
 * stale pre-v2 session, an expired token -> 401, or a 403 saying the session no
 * longer resolves to a company membership).
 *
 */

export const AUTH_NOTICE_KEY = 'authNotice'
export const SESSION_EXPIRED = 'session_expired'

/** localStorage keys holding the acting company. They must not outlive the session:
 *  a stale company id here gets reapplied on the next login and points requests at
 *  the wrong company. */
const COMPANY_LOCAL_STORAGE_KEYS = ['talentinoCompanyId', 'currentCompany', 'previousCompany']

/** Expire every JS-readable cookie (leftover v1 identity cookies, CSRF). httpOnly
 *  cookies (v1 `session`, v2 token) can only be cleared server-side on logout. */
function clearReadableCookies() {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    }
  })
}

/** Drop everything that persists the session client-side: readable cookies plus the
 *  acting-company keys in localStorage. */
export function clearStoredSessionState() {
  clearReadableCookies()
  COMPANY_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
}

/**
 * A deliberate logout tears the session down on purpose, so the "your session
 * expired" notice would be wrong. It also races: clearing the token server-side
 * makes any request still in flight 401, which would otherwise trip forceLogout.
 * endSession() raises this flag first so those 401s stay silent.
 */
let intentionalLogout = false

export function beginIntentionalLogout() {
  intentionalLogout = true
}

export function forceLogout(reason = SESSION_EXPIRED) {
  // A deliberate logout is already tearing things down and doing its own redirect.
  if (intentionalLogout) return
  try {
    sessionStorage.setItem(AUTH_NOTICE_KEY, reason)
  } catch {
  }
  clearStoredSessionState()
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

export default forceLogout
